import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Edit2, Save, X, TrendingUp, Users, DollarSign, 
  Calendar, CheckCircle, XCircle, Clock, AlertTriangle, FileText, Download, Receipt, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateDetailsPage = () => {
  const { affiliateId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [data, setData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'customers', 'commissions', 'billing'
  const [resendingEmail, setResendingEmail] = useState(false);

  useEffect(() => {
    loadData();
  }, [affiliateId]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(
        `${API_URL}/api/superadmin/affiliates/${affiliateId}/details`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setData(response.data);
      setEditForm({
        status: response.data.affiliate.status,
        vat_mode: response.data.affiliate.vat_mode,
        vat_rate: response.data.affiliate.vat_rate,
        first_name: response.data.affiliate.first_name,
        last_name: response.data.affiliate.last_name
      });
      
      setError('');
    } catch (err) {
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess('');
    setError('');
    
    const token = localStorage.getItem('token');
    
    try {
      await axios.put(
        `${API_URL}/api/superadmin/affiliates/${affiliateId}/update`,
        editForm,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setSuccess('Affiliate erfolgreich aktualisiert');
      setEditMode(false);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustCommission = async () => {
    if (!selectedCommission || !adjustmentAmount) return;
    
    const token = localStorage.getItem('token');
    
    try {
      await axios.put(
        `${API_URL}/api/superadmin/affiliates/commissions/${selectedCommission.id}/adjust`,
        {
          new_amount: parseFloat(adjustmentAmount),
          reason: adjustmentReason
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setSuccess('Provision angepasst');
      setSelectedCommission(null);
      setAdjustmentAmount('');
      setAdjustmentReason('');
      await loadData();
    } catch (err) {
      setError('Fehler beim Anpassen');
    }
  };
  const handleResendWelcomeEmail = async () => {
    if (!window.confirm(`Willkommens-E-Mail mit neuem Passwort-Link erneut an ${affiliate.email} senden?`)) return;
    
    const token = localStorage.getItem('token');
    setResendingEmail(true);
    setSuccess('');
    setError('');
    
    try {
      const res = await axios.post(
        `${API_URL}/api/superadmin/affiliates/${affiliateId}/resend-welcome-email`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSuccess(`✅ E-Mail gesendet an ${res.data.email_sent_to} – Link gültig bis ${new Date(res.data.token_expires).toLocaleString('de-DE')}`);
      setTimeout(() => setSuccess(''), 10000);
    } catch (err) {
      setError('E-Mail-Versand fehlgeschlagen: ' + (err.response?.data?.detail || 'Unbekannter Fehler'));
    } finally {
      setResendingEmail(false);
    }
  };

  const handleApproveCommission = async (commissionId) => {
    const token = localStorage.getItem('token');
    
    if (!confirm('Provision freigeben? Status wird auf "approved" gesetzt.')) {
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/superadmin/affiliates/commissions/${commissionId}/approve`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setSuccess('Provision freigegeben');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err) {
      setError('Fehler beim Freigeben: ' + (err.response?.data?.detail || 'Unbekannter Fehler'));
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleMarkPaid = async (commissionId) => {
    const token = localStorage.getItem('token');
    
    const paymentRef = prompt('Zahlungsreferenz (optional):');
    
    if (!confirm('Als bezahlt markieren? Gutschrift wird generiert.')) {
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/superadmin/affiliates/commissions/${commissionId}/mark-paid`,
        { payment_reference: paymentRef || '' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setSuccess('Als bezahlt markiert & Gutschrift generiert');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err) {
      setError('Fehler beim Markieren: ' + (err.response?.data?.detail || 'Unbekannter Fehler'));
      setTimeout(() => setError(''), 5000);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <button
          onClick={() => navigate('/superadmin/affiliates')}
          className="flex items-center gap-2 mb-6 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          data-testid="back-button-loading"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Zurück zum Affiliate-Dashboard</span>
        </button>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <button
          onClick={() => navigate('/superadmin/affiliates')}
          className="flex items-center gap-2 mb-6 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          data-testid="back-button-error"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Zurück zum Affiliate-Dashboard</span>
        </button>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400">Affiliate nicht gefunden</p>
        </div>
      </div>
    );
  }

  const { affiliate, customers, metrics, monthly_commissions, recent_payments } = data;

  return (
    <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen w-full max-w-full overflow-x-hidden">
      <div className="max-w-7xl mx-auto min-w-0">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <button
              onClick={() => navigate('/superadmin/affiliates')}
              data-testid="back-button-detail"
              className="flex items-center gap-2 p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg flex-shrink-0 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              title="Zurück zum Affiliate-Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">Zurück</span>
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100 truncate">
                {affiliate.first_name} {affiliate.last_name}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                {affiliate.email} • {affiliate.affiliate_code}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
            {/* Willkommens-E-Mail erneut senden (nur bei aktiven Affiliates) */}
            {affiliate.status === 'active' && !editMode && (
              <button
                data-testid="resend-welcome-email-btn"
                onClick={handleResendWelcomeEmail}
                disabled={resendingEmail}
                title="Willkommens-E-Mail mit neuem Passwort-Link erneut senden"
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center justify-center gap-2 text-sm min-h-[44px] disabled:opacity-50"
              >
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">{resendingEmail ? 'Sendet...' : 'E-Mail senden'}</span>
                <span className="sm:hidden">{resendingEmail ? '...' : 'Mail'}</span>
              </button>
            )}
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="flex-1 sm:flex-initial px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-sm min-h-[44px]"
              >
                <Edit2 className="w-4 h-4" />
                <span className="hidden sm:inline">Bearbeiten</span>
                <span className="sm:hidden">Edit</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => setEditMode(false)}
                  className="flex-1 sm:flex-initial px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 flex items-center justify-center min-h-[44px]"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 sm:flex-initial px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50 text-sm min-h-[44px]"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-700">{success}</p>
          </div>
        )}
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Performance KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Lifetime Umsatz
              </h3>
              <TrendingUp className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(metrics.lifetime_revenue)}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Lifetime Provision
              </h3>
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(metrics.lifetime_commission)}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Aktuelle Stufe
              </h3>
              <Calendar className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
              {metrics.current_tier}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Kunden
              </h3>
              <Users className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
              {data.active_customers_count}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              von {data.total_customers_count} gesamt
            </p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          <div className="flex w-full overflow-x-auto border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 font-medium transition-colors whitespace-nowrap min-h-[44px] text-sm sm:text-base ${
                activeTab === 'overview'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Edit2 className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">Übersicht & Bearbeitung</span>
              <span className="sm:hidden">Übersicht</span>
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 font-medium transition-colors whitespace-nowrap min-h-[44px] text-sm sm:text-base ${
                activeTab === 'customers'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Users className="w-4 h-4 flex-shrink-0" />
              Kunden ({data.total_customers_count || 0})
            </button>
            <button
              onClick={() => setActiveTab('commissions')}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 font-medium transition-colors whitespace-nowrap min-h-[44px] text-sm sm:text-base ${
                activeTab === 'commissions'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <DollarSign className="w-4 h-4 flex-shrink-0" />
              Provisionen ({monthly_commissions.length})
            </button>
            <button
              onClick={() => setActiveTab('billing')}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 font-medium transition-colors whitespace-nowrap min-h-[44px] text-sm sm:text-base ${
                activeTab === 'billing'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Receipt className="w-4 h-4 flex-shrink-0" />
              Abrechnung
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Basisdaten */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6">
                Basisdaten
              </h2>
              
              {editMode ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Vorname
                      </label>
                      <input
                        type="text"
                        value={editForm.first_name}
                        onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 text-sm sm:text-base min-h-[44px]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nachname
                      </label>
                      <input
                        type="text"
                        value={editForm.last_name}
                        onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 text-sm sm:text-base min-h-[44px]"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 text-sm sm:text-base min-h-[44px]"
                    >
                      <option value="pending">Pending</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      USt-Modus
                    </label>
                    <select
                      value={editForm.vat_mode}
                      onChange={(e) => setEditForm({...editForm, vat_mode: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 text-sm sm:text-base min-h-[44px]"
                    >
                      <option value="small_business">Kleinunternehmer</option>
                      <option value="standard">Regelbesteuert</option>
                    </select>
                  </div>
                  
                  {editForm.vat_mode === 'standard' && (
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        USt-Satz (%)
                      </label>
                      <input
                        type="number"
                        value={editForm.vat_rate}
                        onChange={(e) => setEditForm({...editForm, vat_rate: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-900 text-sm sm:text-base min-h-[44px]"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-200 dark:border-gray-700 gap-1 sm:gap-0">
                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Status</span>
                    <span className={`px-2 py-1 rounded text-xs sm:text-sm font-medium w-fit ${
                      affiliate.status === 'active' ? 'bg-green-100 text-green-800' :
                      affiliate.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {affiliate.status}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-200 dark:border-gray-700 gap-1 sm:gap-0">
                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">E-Mail</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 break-all">{affiliate.email}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-200 dark:border-gray-700 gap-1 sm:gap-0">
                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Registriert</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">{formatDate(affiliate.created_at)}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-200 dark:border-gray-700 gap-1 sm:gap-0">
                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Genehmigt</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">{formatDate(affiliate.admin_approved_at)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Steuer & Abrechnung */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6">
                Steuer & Abrechnung
              </h2>
              
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-200 dark:border-gray-700 gap-1 sm:gap-0">
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">USt-Modus</span>
                  <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                    {affiliate.vat_mode === 'small_business' ? 'Kleinunternehmer' : 'Regelbesteuert'}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-200 dark:border-gray-700 gap-1 sm:gap-0">
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">USt-Satz</span>
                  <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                    {affiliate.vat_mode === 'small_business' ? (
                      <span className="text-gray-500">
                        — (Keine USt-Ausweisung)
                      </span>
                    ) : (
                      `${affiliate.vat_rate}%`
                    )}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-200 dark:border-gray-700 gap-1 sm:gap-0">
                  <span className="text-gray-600 dark:text-gray-400">IBAN</span>
                  <span className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">
                    {affiliate.iban_formatted || affiliate.iban || '-'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Bank</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{affiliate.bank_name || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">BIC</span>
                  <span className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">{affiliate.bic || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Kontoinhaber</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{affiliate.account_holder || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6">
              Geworbene Kunden
            </h2>
            
            {customers && customers.length > 0 ? (
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 dark:bg-gray-900 z-10">Firma</th>
                      <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">E-Mail</th>
                      <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                      <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MRR</th>
                      <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Registriert</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {customers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 sticky left-0 bg-white dark:bg-gray-800 z-10">
                          {customer.company_name || '-'}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:table-cell">
                          {customer.contact_email}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                          {customer.plan || '-'}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            customer.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {customer.status}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(customer.mrr || 0)}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell">
                          {formatDate(customer.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-12">
                Noch keine Kunden geworben
              </p>
            )}
          </div>
        )}

        {/* Commissions Tab */}
        {activeTab === 'commissions' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6">
              Monatliche Provisionen
            </h2>
            
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 dark:bg-gray-900 z-10">Periode</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Umsatz</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Tier</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provision</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {monthly_commissions.map((comm) => (
                    <tr key={comm.id}>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium sticky left-0 bg-white dark:bg-gray-800 z-10">{comm.period}</td>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">{formatCurrency(comm.revenue_total_net)}</td>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm hidden sm:table-cell">
                        <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded text-xs">
                          {comm.tier_percent}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-bold text-green-600">
                        {formatCurrency(comm.commission_amount)}
                        {comm.adjusted && (
                          <span className="ml-2 text-xs text-orange-600">(angepasst)</span>
                        )}
                        {comm.storno_deduction > 0 && (
                          <span className="ml-2 text-xs text-red-600" data-testid={`sa-storno-${comm.period}`} title="Erstattung/Storno verrechnet">−{formatCurrency(comm.storno_deduction)}</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">
                        {comm.on_hold ? (
                          <span className="px-2 py-1 rounded text-xs bg-orange-100 text-orange-700" data-testid={`sa-hold-${comm.period}`} title="Gesperrt (offener Chargeback) – nicht auszahlbar">🔒 Gesperrt</span>
                        ) : (
                        <span className={`px-2 py-1 rounded text-xs ${
                          comm.status === 'paid' ? 'bg-green-100 text-green-600' :
                          comm.status === 'approved' ? 'bg-blue-100 text-blue-600' :
                          comm.status === 'storno' ? 'bg-red-100 text-red-600' :
                          'bg-yellow-100 text-yellow-600'
                        }`}>
                          {comm.status}
                        </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-right">
                        <div className="flex justify-end gap-2">
                          {comm.status === 'calculated' && (
                            <button
                              onClick={() => handleApproveCommission(comm.id)}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 min-h-[32px]"
                            >
                              Freigeben
                            </button>
                          )}
                          {comm.status !== 'paid' && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedCommission(comm);
                                  setAdjustmentAmount(comm.commission_amount.toString());
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs min-h-[32px]"
                              >
                                Anpassen
                              </button>
                              <button
                                onClick={() => handleMarkPaid(comm.id)}
                                className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 min-h-[32px]"
                              >
                                Als bezahlt
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              Gutschriften (Self-Billing)
            </h2>
            
            <div className="space-y-4">
              {monthly_commissions.filter(c => c.status === 'paid').length > 0 ? (
                monthly_commissions.filter(c => c.status === 'paid').map((comm) => (
                  <div key={comm.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          Gutschrift {comm.period}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {formatCurrency(comm.commission_amount)} • Ausgezahlt am {formatDate(comm.payout_date)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        // TODO: Download PDF
                        alert('PDF-Download wird implementiert');
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-12">
                  Noch keine Gutschriften vorhanden
                </p>
              )}
            </div>
          </div>
        )}

        {/* Adjustment Modal */}
        {selectedCommission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4">Provision anpassen</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Periode</label>
                  <p className="text-lg font-bold">{selectedCommission.period}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Neuer Betrag (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={adjustmentAmount}
                    onChange={(e) => setAdjustmentAmount(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Grund</label>
                  <textarea
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="3"
                    placeholder="z.B. Kulanzkorrektur, Fehlerbereinigung..."
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setSelectedCommission(null)}
                  className="px-4 py-2 bg-gray-200 rounded-lg"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleAdjustCommission}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateDetailsPage;
